import React, { useState } from 'react'
import { cn } from '../../lib/utils'
import ActivityLog from './ActivityLog'
import JobSearches from './JobSearches'
import { useCandidate } from './CandidateProvider'
import JobAlerts from './JobAlerts'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import SpecialEvents from './SpecialEvents'
import CoachHistory from './CoachHistory'

export const useCandidateActivity = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/candidate_activity.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ['coaching_summary', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving candidate activity:', error)
    return null
  }

  const {
    candidateAchievements = [],
    candidateActivities = [],
    candidateJobSearches = [],
    candidateJobAlerts,
    candidateSpecialEvents = [],
    postLandingSupport = false,
  } = data || {}
  return {
    candidateAchievements,
    candidateActivities,
    candidateJobSearches,
    candidateJobAlerts,
    candidateSpecialEvents,
    postLandingSupport,
    isFetching,
    refetch,
  }
}

const tabMap = {
  platform_progress: 'Platform Progress',
  activity_log: 'Activity Log',
  job_searches: 'Job Searches',
  job_alerts: 'Job Alerts',
  special_events: 'Special Events',
  coach_history: 'Coach History',
}

const CandidateActivitySection = ({ className }) => {
  let initialTabKey = 'platform_progress'
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    const urlTab = params.get('sub_tab')
    if (urlTab && tabMap[urlTab]) initialTabKey = urlTab
  }
  const [selectedTabKey, setSelectedTabKey] = useState(initialTabKey)

  const tabs = [
    'platform_progress',
    'activity_log',
    'job_searches',
    'job_alerts',
    'special_events',
    'coach_history',
  ]

  return (
    <div
      className={cn(
        'my-8 flex flex-col justify-between divide-y divide-[#B9B9B9] md:mt-0',
        className,
      )}
    >
      <Page
        tabs={tabs}
        selectedTabKey={selectedTabKey}
        setSelectedTabKey={setSelectedTabKey}
      />
    </div>
  )
}

function chunkArray(array, chunkSize) {
  const chunks = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

const PlatformProgress = () => {
  const { candidateAchievements } = useCandidateActivity()
  const sections = Object.entries(candidateAchievements)

  // Distribute sections into 3 roughly equal columns
  const columnChunks = chunkArray(sections, Math.ceil(sections.length / 3))

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {columnChunks.map((column, colIndex) => (
        <div key={`columnChunk-${colIndex}`} className="flex flex-col gap-6">
          {column.map(([sectionTitle, items]) => (
            <div key={sectionTitle}>
              <h2 className="mb-2 text-lg font-bold text-gray-800">
                {sectionTitle}
              </h2>
              <ul className="space-y-1">
                {items.map((item) => (
                  <li
                    key={`${sectionTitle}-${item.key}`}
                    className="flex items-start gap-2"
                  >
                    <input
                      type="checkbox"
                      checked={item.achieved}
                      readOnly
                      className="mt-1 accent-green-600"
                    />
                    <span
                      className={
                        item.achieved ? 'text-gray-900' : 'text-gray-500'
                      }
                    >
                      {item.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const Page = ({ tabs, selectedTabKey, setSelectedTabKey }) => {
  return (
    <div className="flex w-full gap-8">
      <div className="flex w-max min-w-56 flex-col gap-4">
        {tabs.map((tabKey, idx) => {
          const selectedClass =
            selectedTabKey === tabKey ? 'text-[#fff] bg-intoo-blue-medium' : ''
          return (
            <div
              className={cn(
                `cursor-pointer rounded-[10px] px-5 py-[10px] text-[#333] checkbox-${tabKey}`,
                selectedClass,
              )}
              key={`tabs-list-${idx}`}
              onClick={() => setSelectedTabKey(tabKey)}
              onFocus={() => setSelectedTabKey(tabKey)}
              tabIndex={0}
              role="button"
            >
              <div className="grid grid-cols-5">
                <div className="col-span-4">{tabMap[tabKey]}</div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="w-full">
        {selectedTabKey === 'platform_progress' && <PlatformProgress />}
        {selectedTabKey === 'activity_log' && <ActivityLog />}
        {selectedTabKey === 'job_searches' && <JobSearches />}
        {selectedTabKey === 'job_alerts' && <JobAlerts />}
        {selectedTabKey === 'special_events' && <SpecialEvents />}
        {selectedTabKey === 'coach_history' && <CoachHistory />}
      </div>
    </div>
  )
}

export default CandidateActivitySection
